import { useState, useEffect } from 'preact/hooks';
import { useAuth } from './useAuth';
import type { CurrentContext } from '../types';

/**
 * Hook to detect current scope (team/none) from URL
 *
 * Detects team context from URL patterns:
 * - /teams/:teamId/* -> team scope
 * - /t/:teamId/* -> team scope (legacy)
 * - everything else -> no scope
 *
 * Returns context with teamId and userId when in team scope.
 * Updates automatically on route changes.
 *
 * @returns CurrentContext object with scope, teamId, teamName, and userId
 */
export function useCurrentContext(): CurrentContext {
  const { user } = useAuth();

  // Initialize context immediately from current URL
  const getInitialContext = (): CurrentContext => {
    const path = window.location.pathname;
    const userId = user?.id || user?.sub;

    if (path.startsWith('/teams/')) {
      const teamIdMatch = path.match(/^\/teams\/([^\/]+)/);
      const teamId = teamIdMatch?.[1];
      if (teamId) {
        return {
          scope: 'team',
          teamId,
          userId,
          teamName: `Team ${teamId.slice(0, 8)}`
        };
      }
    } else if (path.startsWith('/t/')) {
      const teamIdMatch = path.match(/^\/t\/([^\/]+)/);
      const teamId = teamIdMatch?.[1];
      if (teamId) {
        return {
          scope: 'team',
          teamId,
          userId,
          teamName: `Team ${teamId.slice(0, 8)}`
        };
      }
    }
    return { scope: 'none' };
  };

  const [context, setContext] = useState<CurrentContext>(getInitialContext);

  useEffect(() => {
    const updateContext = () => {
      const path = window.location.pathname;
      const userId = user?.id || user?.sub;

      if (path.startsWith('/teams/')) {
        // Extract teamId from /teams/:teamId/*
        const teamIdMatch = path.match(/^\/teams\/([^\/]+)/);
        const teamId = teamIdMatch?.[1];

        if (teamId) {
          setContext({
            scope: 'team',
            teamId,
            userId,
            // TODO: Fetch team name from API when available
            teamName: `Team ${teamId.slice(0, 8)}` // Placeholder for now
          });
        } else {
          setContext({ scope: 'none' });
        }
      } else if (path.startsWith('/t/')) {
        // Extract teamId from /t/:teamId/* (legacy route)
        const teamIdMatch = path.match(/^\/t\/([^\/]+)/);
        const teamId = teamIdMatch?.[1];

        if (teamId) {
          setContext({
            scope: 'team',
            teamId,
            userId,
            // TODO: Fetch team name from API when available
            teamName: `Team ${teamId.slice(0, 8)}` // Placeholder for now
          });
        } else {
          setContext({ scope: 'none' });
        }
      } else {
        setContext({ scope: 'none' });
      }
    };

    // Update on mount
    updateContext();

    // Listen for route changes
    const handleRouteChange = () => updateContext();
    window.addEventListener('popstate', handleRouteChange);

    // Also listen for pushState/replaceState (for SPA navigation)
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      originalPushState.apply(this, args);
      setTimeout(updateContext, 0);
    };

    history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      setTimeout(updateContext, 0);
    };

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
    };
  }, [user?.id, user?.sub]);

  return context;
}
